import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import { Button, Table } from 'semantic-ui-react';

import Toggle from '@components/toggle';

import { stableStringify, summarizeMode } from './madrobot-modal.utils';
import type { Rule } from './madrobot.models';
import s from './madrobot-modal.scss';

const DragHandle = SortableHandle(() => (
  <GripVertical aria-label="Drag to reorder" className={s.dragHandleIcon} />
));

// Row accent: red when armed, enabled, and saved (injecting now); yellow when
// armed with a pending change to its injection status; gray otherwise.
const accentClassFor = (
  rule: Rule,
  armed: boolean,
  saved: boolean,
  serverActive: boolean,
): string => {
  if (!armed) return s.rowInactive;
  if (rule.enabled) return saved ? s.rowActive : s.rowPending;
  // Disabled locally but still live on the server (an unsaved disable).
  return serverActive ? s.rowPending : s.rowInactive;
};

interface SortableRuleRowProps {
  rule: Rule;
  armed: boolean;
  saved: boolean;
  serverActive: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onEdit: (rule: Rule) => void;
  onDelete: (id: string) => void;
}

const SortableRuleRow = SortableElement<SortableRuleRowProps>(
  ({
    rule,
    armed,
    saved,
    serverActive,
    onToggle,
    onEdit,
    onDelete,
  }: SortableRuleRowProps) => (
    <Table.Row className={accentClassFor(rule, armed, saved, serverActive)}>
      <Table.Cell collapsing className={s.dragHandleCell}>
        <DragHandle />
      </Table.Cell>
      <Table.Cell collapsing verticalAlign="middle">
        <div className={s.toggleCell}>
          <Toggle
            aria-label={`Enable rule ${rule.name}`}
            checked={rule.enabled}
            onChange={(_, { checked }) => onToggle(rule.id, !!checked)}
          />
        </div>
      </Table.Cell>
      <Table.Cell>{rule.name}</Table.Cell>
      <Table.Cell>{rule.method}</Table.Cell>
      <Table.Cell className={s.pattern}>{rule.pathPattern}</Table.Cell>
      <Table.Cell>{summarizeMode(rule.mode)}</Table.Cell>
      <Table.Cell>{rule.probability.toFixed(2)}</Table.Cell>
      <Table.Cell collapsing className={s.rowActions}>
        <Button
          aria-label={`Edit rule ${rule.name}`}
          icon={<Pencil />}
          onClick={() => onEdit(rule)}
          size="mini"
        />
        <Button
          aria-label={`Delete rule ${rule.name}`}
          icon={<Trash2 />}
          onClick={() => onDelete(rule.id)}
          size="mini"
        />
      </Table.Cell>
    </Table.Row>
  ),
);

interface SortableRulesBodyProps {
  rules: Rule[];
  armed: boolean;
  savedSignatures: Set<string>;
  serverEnabledIds: Set<string>;
  onToggle: (id: string, enabled: boolean) => void;
  onEdit: (rule: Rule) => void;
  onDelete: (id: string) => void;
}

export const SortableRulesBody = SortableContainer<SortableRulesBodyProps>(
  ({
    rules,
    armed,
    savedSignatures,
    serverEnabledIds,
    onToggle,
    onEdit,
    onDelete,
  }: SortableRulesBodyProps) => (
    <Table.Body>
      {rules.map((rule, index) => (
        <SortableRuleRow
          key={rule.id}
          index={index}
          rule={rule}
          armed={armed}
          saved={savedSignatures.has(stableStringify(rule))}
          serverActive={serverEnabledIds.has(rule.id)}
          onToggle={onToggle}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Table.Body>
  ),
);
